import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Clock, Users, MapPin, Star } from 'lucide-react';
import axios from 'axios';
import { cardThumbnail, cardThumbnailSrcSet } from '../utils/cloudinaryOptimize';

const API = process.env.REACT_APP_BACKEND_URL;

const ListingCard = ({ listing }) => (
  <Link
    to={`/listing/${listing.id}`}
    className="block bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 hover:shadow-md dark:hover:shadow-lg transition-all overflow-hidden group"
    data-testid={`recommendation-${listing.id}`}
  >
    {listing.image_url ? (
      <div className="h-28 overflow-hidden">
        <img
          src={cardThumbnail(listing.image_url)}
          srcSet={cardThumbnailSrcSet(listing.image_url)}
          alt={listing.title}
          loading="lazy"
          className="w-full h-full object-cover group-hover:scale-105 transition-transform"
        />
      </div>
    ) : (
      <div className="h-28 bg-slate-100 dark:bg-slate-700 flex items-center justify-center">
        <MapPin className="h-6 w-6 text-slate-300 dark:text-slate-500" />
      </div>
    )}
    <div className="p-3">
      <h4 className="font-medium text-sm text-slate-800 dark:text-slate-200 truncate group-hover:text-[#D4A017] transition-colors">{listing.title}</h4>
      <div className="flex items-center gap-1.5 mt-1 text-xs text-slate-500 dark:text-slate-400">
        <MapPin className="h-3 w-3" />
        <span className="truncate">{listing.town}</span>
      </div>
      {listing.rating > 0 && (
        <div className="flex items-center gap-1 mt-1">
          <Star className="h-3 w-3 text-amber-400 fill-amber-400" />
          <span className="text-xs text-slate-600 dark:text-slate-400">{listing.rating}</span>
        </div>
      )}
    </div>
  </Link>
);

const RecentlyViewedSection = ({ currentListingId }) => {
  const [recentlyViewed, setRecentlyViewed] = useState([]);
  const [alsoViewed, setAlsoViewed] = useState([]);

  useEffect(() => {
    // Load recently viewed from localStorage
    try {
      const stored = JSON.parse(localStorage.getItem('recentlyViewed') || '[]');
      const arr = Array.isArray(stored) ? stored : [];
      setRecentlyViewed(arr.filter(l => l && l.id !== currentListingId).slice(0, 6));
    } catch {}

    // Fetch "also viewed" from backend
    axios.get(`${API}/api/listings/${currentListingId}/also-viewed?limit=6`)
      .then(res => setAlsoViewed(Array.isArray(res.data) ? res.data : []))
      .catch(() => {});
  }, [currentListingId]);

  if (recentlyViewed.length === 0 && alsoViewed.length === 0) return null;

  return (
    <div className="space-y-8 mt-8" data-testid="recommendations-section">
      {alsoViewed.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-4">
            <Users className="h-4 w-4 text-[#4A6741]" />
            <h3 className="font-semibold text-lg text-slate-800 dark:text-slate-200">People Also Viewed</h3>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {alsoViewed.map(l => <ListingCard key={l.id} listing={l} />)}
          </div>
        </div>
      )}

      {recentlyViewed.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-4">
            <Clock className="h-4 w-4 text-slate-500 dark:text-slate-400" />
            <h3 className="font-semibold text-lg text-slate-800 dark:text-slate-200">Recently Viewed</h3>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {recentlyViewed.map(l => <ListingCard key={l.id} listing={l} />)}
          </div>
        </div>
      )}
    </div>
  );
};

export default RecentlyViewedSection;
